import React, { useState } from 'react';
import { CloudArrowUpIcon, XMarkIcon } from '../../icons';
import { useNotification } from '../../contexts/NotificationContext';
import { recordingAPI } from '../../services/api';
import CaseReferenceField from '../recording/CaseReferenceField';
import LanguageSelect from '../recording/LanguageSelect';
import SessionMetadataBar from '../recording/SessionMetadataBar';
import DuplicateSessionDialog from '../recording/DuplicateSessionDialog';
import {
  extractDuplicateSession,
  validateUploadFile,
} from '../../utils/recordingForm';

const UploadModal = ({
  open,
  onClose,
  tenantId,
  courtDisplayName,
  initiatorDisplayName,
  languages,
  onUploadSuccess,
  onDuplicateSession,
}) => {
  const {
    showProcessingNotification,
    showErrorNotification,
    updateNotification,
  } = useNotification();

  const [uploadCaseReference, setUploadCaseReference] = useState('');
  const [uploadLanguage, setUploadLanguage] = useState('');
  const [fileValidationError, setFileValidationError] = useState('');
  const [duplicateSession, setDuplicateSession] = useState(null);
  const [uploadFailed, setUploadFailed] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);

  const canUploadFile =
    selectedFile &&
    uploadCaseReference.trim().length > 0 &&
    uploadLanguage.length > 0 &&
    Boolean(tenantId) &&
    !fileValidationError;

  const resetUploadForm = () => {
    setUploadCaseReference('');
    setUploadLanguage('');
    setSelectedFile(null);
    setFileValidationError('');
    setDuplicateSession(null);
    setUploadFailed(false);
    setUploadProgress(0);
    setIsUploading(false);
  };

  const handleDuplicate = (error) => {
    const duplicate = extractDuplicateSession(error);
    if (duplicate) {
      setDuplicateSession(duplicate);
      onDuplicateSession?.(duplicate);
      return true;
    }
    return false;
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const validation = validateUploadFile(file);
    if (!validation.valid) {
      setFileValidationError(validation.error);
      setSelectedFile(null);
      return;
    }

    setFileValidationError('');
    setSelectedFile(file);
    setUploadFailed(false);
  };

  const performUpload = async () => {
    const resp = await recordingAPI.uploadRecordingChunked(
      tenantId,
      {
        file: selectedFile,
        caseReference: uploadCaseReference.trim(),
        language: uploadLanguage,
      },
      (progress) => setUploadProgress(progress)
    );
    const data = resp?.data?.data || resp?.data || {};
    if (data.validation_status === 'failed') {
      const error = new Error(data.failure_reason || 'Recording failed validation');
      error.response = {
        data: {
          error: data.failure_reason || 'Recording failed validation',
          message: data.failure_reason || 'Recording failed validation',
          code: 'validation_failed',
        },
      };
      throw error;
    }
    setUploadProgress(100);
    return resp;
  };

  const handleUploadFile = async () => {
    if (!selectedFile) {
      showErrorNotification('No File Selected', 'Please select a file to upload.');
      return;
    }

    if (!tenantId) {
      showErrorNotification('Missing court', 'Your account is not assigned to a court tenant.');
      return;
    }

    const validation = validateUploadFile(selectedFile);
    if (!validation.valid) {
      setFileValidationError(validation.error);
      showErrorNotification('Invalid file', validation.error);
      return;
    }

    if (!uploadCaseReference.trim() || !uploadLanguage) {
      showErrorNotification(
        'Missing information',
        'Case reference and language are required before uploading.'
      );
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);
    setUploadFailed(false);
    setDuplicateSession(null);

    const notificationId = showProcessingNotification(
      'Processing Audio',
      'Uploading audio and queuing English transcription via Dual-Route API...'
    );

    const isLargeFile = selectedFile.size > 50 * 1024 * 1024;

    try {
      const resp = await performUpload();
      const data = resp?.data?.data || resp?.data || {};

      updateNotification(notificationId, {
        type: 'success',
        title: 'Recording Queued',
        message: isLargeFile
          ? 'Large file uploaded. English transcription will run in the background.'
          : 'Uploaded. English transcription is being generated from the audio.',
        icon: '✅',
        color: 'green',
        duration: isLargeFile ? 10000 : 5000,
      });

      onUploadSuccess?.({
        id: data.upload_id || data.transcription_job?.job_id,
        session_id: data.upload_id || data.transcription_job?.job_id,
        case_reference: uploadCaseReference.trim(),
        title: uploadCaseReference.trim(),
        status: 'uploading',
        created_at: new Date().toISOString(),
      });
      onClose();
      resetUploadForm();
    } catch (error) {
      if (handleDuplicate(error)) {
        updateNotification(notificationId, {
          type: 'error',
          title: 'Duplicate session',
          message: 'This case may already have a recording.',
          icon: '⚠️',
          color: 'orange',
          duration: 8000,
        });
        setUploadFailed(true);
      } else if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        updateNotification(notificationId, {
          type: 'error',
          title: 'Upload Timeout',
          message: 'Large file upload timed out. The file may be processing in the background.',
          icon: '⏰',
          color: 'orange',
          duration: 10000,
        });
        setUploadFailed(true);
      } else {
        const body = error.response?.data || {};
        if (body.code === 'unsupported_format') {
          const allowed = Array.isArray(body.allowed) ? body.allowed.join(', ') : 'WAV, MP3, MP4, M4A';
          const message = body.message || body.error || `Unsupported format. Allowed: ${allowed}.`;
          setFileValidationError(message);
        }
        updateNotification(notificationId, {
          type: 'error',
          title: body.code === 'unsupported_format' ? 'Unsupported format' : 'Upload Failed',
          message:
            body.message ||
            body.error ||
            error.message ||
            'Failed to upload audio file. You can retry from the upload dialog.',
          icon: '❌',
          color: 'red',
          duration: 7000,
        });
        setUploadFailed(true);
      }
    } finally {
      setIsUploading(false);
    }
  };

  const handleCancelUpload = () => {
    onClose();
    resetUploadForm();
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-3 sm:p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="p-4 sm:p-6 border-b border-legal-200">
          <div className="flex justify-between items-center">
            <h3 className="text-lg sm:text-xl font-semibold text-legal-900">
              {isUploading ? 'Uploading Recording' : uploadFailed ? 'Upload Failed' : 'Upload Recording'}
            </h3>
            <button
              type="button"
              onClick={handleCancelUpload}
              disabled={isUploading}
              className="text-legal-400 hover:text-legal-600 disabled:opacity-50"
            >
              <XMarkIcon className="h-5 w-5 sm:h-6 sm:w-6" />
            </button>
          </div>
        </div>

        <div className="p-4 sm:p-6">
          {!isUploading ? (
            <div className="space-y-4">
              <CaseReferenceField
                id="upload-case-reference"
                value={uploadCaseReference}
                onChange={setUploadCaseReference}
              />
              <LanguageSelect
                id="upload-language"
                value={uploadLanguage}
                onChange={setUploadLanguage}
                languages={languages}
              />

              <SessionMetadataBar
                caseReference={uploadCaseReference}
                language={uploadLanguage}
                courtName={courtDisplayName}
                initiatorName={initiatorDisplayName}
              />

              {duplicateSession && (
                <DuplicateSessionDialog
                  session={duplicateSession}
                  onDismiss={() => setDuplicateSession(null)}
                />
              )}

              <div>
                <label className="block text-sm font-medium text-legal-700 mb-2">
                  Select Audio or Video File
                </label>
                <div className="border-2 border-dashed border-legal-300 rounded-lg p-6 text-center hover:border-blue-500 transition-colors">
                  <CloudArrowUpIcon className="h-12 w-12 text-legal-400 mx-auto mb-4" />
                  <div className="space-y-2">
                    <p className="text-sm text-legal-600">
                      {selectedFile ? selectedFile.name : 'Click to select or drag and drop'}
                    </p>
                    <p className="text-xs text-legal-500">WAV, MP3, MP4, M4A up to 500MB</p>
                    <input
                      type="file"
                      accept=".wav,.mp3,.mp4,.m4a,audio/*,video/mp4"
                      onChange={handleFileSelect}
                      className="hidden"
                      id="audio-upload"
                    />
                    <label
                      htmlFor="audio-upload"
                      className="inline-block px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm cursor-pointer"
                    >
                      Choose File
                    </label>
                  </div>
                </div>
                {fileValidationError && (
                  <p className="mt-2 text-sm text-red-600">{fileValidationError}</p>
                )}
              </div>

              {selectedFile && (
                <div className="bg-legal-50 rounded-lg p-3">
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-legal-600">File:</span>
                    <span className="font-medium text-legal-900">{selectedFile.name}</span>
                  </div>
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-legal-600">Size:</span>
                    <span className="font-medium text-legal-900">
                      {(selectedFile.size / (1024 * 1024)).toFixed(2)} MB
                    </span>
                  </div>
                </div>
              )}

              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={handleCancelUpload}
                  className="flex-1 px-4 py-2 text-legal-600 border border-legal-300 rounded-lg hover:bg-legal-100 transition-colors text-sm"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleUploadFile}
                  disabled={!canUploadFile}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                >
                  {uploadFailed ? 'Retry Upload' : 'Upload File'}
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="text-center py-4">
                <div className="w-20 h-20 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse">
                  <CloudArrowUpIcon className="h-10 w-10 text-white" />
                </div>
                <h4 className="text-lg font-semibold text-legal-900 mb-2">
                  {selectedFile ? selectedFile.name : 'Uploading Audio'}
                </h4>
                <div className="text-2xl font-mono font-bold text-blue-600 mb-2">{uploadProgress}%</div>
                <p className="text-sm text-legal-600">Uploading your audio file...</p>
              </div>

              <div className="w-full bg-legal-200 rounded-full h-2">
                <div
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>

              {uploadProgress === 100 && (
                <div className="text-center text-green-600 text-sm font-medium">
                  ✓ Upload completed successfully!
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UploadModal;
